import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Alert, Box, Divider, List, ListItem, ListItemSecondaryAction, ListItemText, Typography} from '@mui/material';
import {Button} from '../../components/Button';
import {TextField} from '../../components/TextField';
import {ErrorAlert} from '../../components/ErrorAlert';
import {useTranslation} from 'react-i18next';
import {CloseIcon, IconDeleteInRow, PlusIcon} from '../../components/Icons';
import {Item} from "react-stately";
import {Select} from '../../components/Select';
import {NestedSelect, NestedSelectValue} from '../../components/NestedSelect';
import useAttributeDefinitions from "../../hooks/useAttributeDefinitions";
import {IconButton} from "../../components/IconButton";
import useReportPreview from "../../hooks/useReportPreview";
import {AttributeFilter, FilterOperator} from "../../api/EmployeeApi";
import {
    AggregationFunction,
    ChartType,
    ReportCreateRequest
} from "../../api/ReportApi";
import {useNavigate} from "react-router-dom";
import {useReports} from "../../hooks/useReports";
import {NumberField} from '../../components/NumberField';
import {DatePicker} from '../../components/DatePicker';
import {Checkbox} from '../../components/Checkbox';
import {Chip} from '../../components/Chip';
import {getAttributeConfig, type ImprovedSchema,} from './schemaUtils';
import reportableFieldsSchemaJson from './reportableFields.json';
import TreemapDataChart from "./charts/TreemapDataChart";
import BarDataChart from "./charts/BarDataChart";
import {parseDate} from "@internationalized/date";
import {DateRangePicker} from "../../components/DateRangePicker";
import {DateRange} from "@react-types/calendar";
import {AttributeEntity, ATTRIBUTE_ENTITIES} from "../../api/AttributeDefinitionApi";

const reportableFieldsSchema = reportableFieldsSchemaJson as ImprovedSchema;

/**
 * Helper function to convert AttributeDefinition to NestedItem format
 * This ensures type compatibility with NestedSelect component
 */
const mapAttributeDefinitionsToNestedItems = (definitions: any[]): any[] => {
    if (!definitions) return [];

    return definitions.map(def => ({
        ...def, // Spread all properties from AttributeDefinition
        // Recursively map nested definitions
        nestedAttributeDefinitions: def.nestedAttributeDefinitions
            ? mapAttributeDefinitionsToNestedItems(def.nestedAttributeDefinitions)
            : null
    }));
};

interface ReportCreatorProps {
    saving?: boolean;
}

interface SelectItem {
    id: string;
    name: string;
}

interface ReportGroupingConfig {
    enabled: boolean;
    mode?: string;
}

interface ReportAggregationConfig {
    function: string;
}

const ReportCreator: React.FC<ReportCreatorProps> = ({
                                                         saving = false,
                                                     }) => {

    const {t} = useTranslation();
    const navigate = useNavigate();

    const attributeEntities = Object.entries(ATTRIBUTE_ENTITIES).map(
        ([id, i18nKey]) => ({
            id: id as AttributeEntity,
            name: t(i18nKey),
        })
    );

    // State
    const [reportName, setReportName] = useState('');
    const [reportDescription, setReportDescription] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    // Report configuration state
    const [selectedEntity, setSelectedEntity] = useState<AttributeEntity>("EMPLOYEE");
    const [selectedAttribute, setSelectedAttribute] = useState<string>();
    const [selectedNestedAttribute, setSelectedNestedAttribute] = useState<string>();
    const [selectedAttributeValue, setSelectedAttributeValue] = useState<NestedSelectValue | null>(null);
    const [reportFilters, setReportFilters] = useState<AttributeFilter[]>([]);
    const [groupingConfig, setGroupingConfig] = useState<ReportGroupingConfig>({
        enabled: false,
        mode: undefined
    });
    const [aggregationConfig, setAggregationConfig] = useState<ReportAggregationConfig>({
        function: 'COUNT'
    });
    const [chartType, setChartType] = useState<ChartType>(ChartType.BAR);

    // Filter creation state
    const [isCreatingFilter, setIsCreatingFilter] = useState(false);
    const [filterAttributeName, setFilterAttributeName] = useState<string>('');
    const [filterOperator, setFilterOperator] = useState<FilterOperator>(FilterOperator.EQUALS);
    const [filterValue, setFilterValue] = useState<any>(undefined);
    const [startEnd, setStartEnd] = useState<DateRange | null>(null);

    // Helper function to check if a filter is complete
    const isFilterComplete = useCallback((filter: AttributeFilter): boolean => {
        if (!filter.attributeName || !filter.operator) {
            return false;
        }

        if (filter.operator === FilterOperator.IS_NULL || filter.operator === FilterOperator.IS_NOT_NULL) {
            return true;
        }

        if (filter.operator === FilterOperator.BETWEEN) {
            if (!Array.isArray(filter.value) || filter.value.length !== 2) {
                return false;
            }
            return filter.value[0] !== undefined && filter.value[0] !== null && filter.value[0] !== '' &&
                filter.value[1] !== undefined && filter.value[1] !== null && filter.value[1] !== '';
        }

        return filter.value !== undefined && filter.value !== null && filter.value !== '';
    }, []);

    // Validated filters - only complete filters that should trigger API calls
    const validatedFilters = useMemo(() => {
        return reportFilters.filter(isFilterComplete);
    }, [reportFilters, isFilterComplete]);

    const {
        previewResult,
        loading: loadingPreview,
        reload: reloadPreview
    } = useReportPreview(
        selectedEntity,
        selectedAttribute || 'employeeStatus',
        AggregationFunction.COUNT,
        {type: chartType},
        validatedFilters,
        startEnd,
        selectedNestedAttribute
    );

    const {
        reports,
        loading: loadingReports,
        createReport,
        creating,
        error: reportsError,
    } = useReports();

    const {
        enabledAttributeDefinitions: attributeDefinitions,
        filterableAttributeDefinitions,
        getAttributeDefinitionByName,
        refresh: reloadAttributeDefinitions
    } = useAttributeDefinitions(selectedEntity);

    // Initialize with default filter
    useEffect(() => {
        const initialFilter: AttributeFilter = {
            attributeName: 'employeeStatus',
            operator: FilterOperator.EQUALS,
            value: 'active'
        };
        setReportFilters([initialFilter]);
    }, []);

    useEffect(() => {
        if (selectedEntity) {
            reloadAttributeDefinitions();
        }
    }, [selectedEntity, reloadAttributeDefinitions]);

    // Reload preview when validated filters or attribute changes
    useEffect(() => {
        if (selectedAttribute && validatedFilters.length > 0) {
            reloadPreview();
        }
    }, [validatedFilters, selectedAttribute, selectedNestedAttribute, startEnd, reloadPreview]);

    // Handle attribute selection change
    const handleAttributeSelectionChange = useCallback((value: NestedSelectValue | null) => {
        setSelectedAttributeValue(value);

        if (value) {
            if (value.parentId) {
                // Nested selection - parent becomes the attribute, child becomes the nested attribute
                const parentAttr = attributeDefinitions.find(attr => attr.id === value.parentId);
                if (parentAttr) {
                    setSelectedAttribute(parentAttr.name);
                    setSelectedNestedAttribute(getAttributeNameFromChild(value.childId));
                } else {
                    setSelectedAttribute(getAttributeNameFromChild(value.childId));
                    setSelectedNestedAttribute(undefined);
                }
            } else {
                // Top-level selection
                setSelectedAttribute(getAttributeNameFromChild(value.childId));
                setSelectedNestedAttribute(undefined);
            }
        } else {
            setSelectedAttribute(undefined);
            setSelectedNestedAttribute(undefined);
        }
    }, [attributeDefinitions]);

    // Helper to get attribute name from child ID
    const getAttributeNameFromChild = (childId: number | string): string => {
        // Search in all attributes including nested ones
        for (const attr of attributeDefinitions) {
            if (attr.id === childId) {
                return attr.name;
            }
            if (attr.nestedAttributeDefinitions) {
                const nestedAttr = attr.nestedAttributeDefinitions.find(nested => nested.id === childId);
                if (nestedAttr) {
                    return nestedAttr.name;
                }
            }
        }
        return String(childId);
    };

    // Get the selected attribute configuration from schema (memoized)
    const selectedAttributeConfig = useMemo(() => {
        const attrName = selectedNestedAttribute || selectedAttribute;
        if (!attrName) return null;
        return getAttributeConfig(attrName, reportableFieldsSchema);
    }, [selectedAttribute, selectedNestedAttribute]);

    // Build report configuration
    // const buildReportConfig = useCallback((): ReportConfig => {
    //     const filters: FilterGroup = {
    //         operator: "AND",
    //         filters: validatedFilters.map(f => ({
    //             attributeName: f.attributeName,
    //             operator: f.operator,
    //             value: f.value
    //         }))
    //     };
    //
    //     const grouping = groupingConfig.enabled ? {
    //         mode: groupingConfig.mode as GroupingMode
    //     } : undefined;
    //
    //     const aggregation = {
    //         function: aggregationConfig.function as AggregationFunction
    //     };
    //
    //     const visualization: VisualizationConfig = {
    //         type: chartType
    //     };
    //
    //     return {
    //         groupingAttribute: selectedAttribute!,
    //         filters,
    //         grouping,
    //         aggregation,
    //         visualization
    //     };
    // }, [selectedAttribute, validatedFilters, groupingConfig, aggregationConfig, chartType]);

    // Add filter
    const handleAddFilter = useCallback(() => {
        const newFilter: AttributeFilter = {
            attributeName: filterAttributeName,
            operator: filterOperator,
            value: filterValue
        };

        setReportFilters(prev => [...prev, newFilter]);
        setIsCreatingFilter(false);
        setFilterAttributeName('');
        setFilterOperator(FilterOperator.EQUALS);
        setFilterValue(undefined);
    }, [filterAttributeName, filterOperator, filterValue]);

    // Remove filter
    const handleRemoveFilter = useCallback((filter: AttributeFilter) => {
        setReportFilters(prev => prev.filter(f => f !== filter));
    }, []);

    // Cancel filter creation
    const handleCancelFilter = useCallback(() => {
        setIsCreatingFilter(false);
        setFilterAttributeName('');
        setFilterOperator(FilterOperator.EQUALS);
        setFilterValue(undefined);
    }, []);

    // Get filter label
    const getFilterLabel = useCallback((filter: AttributeFilter): string => {
        const attrDef = getAttributeDefinitionByName(filter.attributeName);
        const attrLabel = attrDef?.label || filter.attributeName;

        let operatorLabel: string;
        switch (filter.operator) {
            case FilterOperator.EQUALS:
                operatorLabel = t('analytics.reportCreator.operators.equals');
                break;
            case FilterOperator.NOT_EQUALS:
                operatorLabel = t('analytics.reportCreator.operators.notEquals');
                break;
            case FilterOperator.GREATER_THAN:
                operatorLabel = t('analytics.reportCreator.operators.greaterThan');
                break;
            case FilterOperator.LESS_THAN:
                operatorLabel = t('analytics.reportCreator.operators.lessThan');
                break;
            case FilterOperator.CONTAINS:
                operatorLabel = t('analytics.reportCreator.operators.contains');
                break;
            // case FilterOperator.STARTS_WITH:
            //     operatorLabel = t('analytics.reportCreator.operators.startsWith');
            //     break;
            // case FilterOperator.ENDS_WITH:
            //     operatorLabel = t('analytics.reportCreator.operators.endsWith');
            //     break;
            case FilterOperator.IS_NULL:
                operatorLabel = t('analytics.reportCreator.operators.isNull');
                break;
            case FilterOperator.IS_NOT_NULL:
                operatorLabel = t('analytics.reportCreator.operators.isNotNull');
                break;
            case FilterOperator.IN:
                operatorLabel = t('analytics.reportCreator.operators.in');
                break;
            case FilterOperator.NOT_IN:
                operatorLabel = t('analytics.reportCreator.operators.notIn');
                break;
            case FilterOperator.BETWEEN:
                operatorLabel = t('analytics.reportCreator.operators.between');
                break;
            default:
                operatorLabel = filter.operator;
        }

        if (filter.operator === FilterOperator.IS_NULL || filter.operator === FilterOperator.IS_NOT_NULL) {
            return `${attrLabel} ${operatorLabel}`;
        }

        if (filter.operator === FilterOperator.BETWEEN && Array.isArray(filter.value)) {
            return `${attrLabel} ${operatorLabel} ${filter.value[0]} - ${filter.value[1]}`;
        }

        return `${attrLabel} ${operatorLabel} ${filter.value}`;
    }, [getAttributeDefinitionByName, t]);

    // Get filter value input component
    const getFilterValueInput = useCallback(() => {
        const attrDef = getAttributeDefinitionByName(filterAttributeName);
        if (!attrDef) return null;

        // For IS_NULL and IS_NOT_NULL, no value input needed
        if (filterOperator === FilterOperator.IS_NULL || filterOperator === FilterOperator.IS_NOT_NULL) {
            return null;
        }

        // For BETWEEN operator with date type
        if (filterOperator === FilterOperator.BETWEEN) {
            if (attrDef.fieldType === 'DATE') {
                return (
                    <DateRangePicker
                        label={t('analytics.reportCreator.filterValue')}
                        value={startEnd}
                        onChange={handleDateRangeChange}
                    />
                );
            }
            // For other types with BETWEEN, could add range inputs here
            return null;
        }

        // Regular value inputs based on field type
        switch (attrDef.fieldType) {
            case 'STRING':
                return (
                    <TextField
                        label={t('analytics.reportCreator.filterValue')}
                        value={filterValue || ''}
                        onChange={setFilterValue}
                    />
                );
            case 'NUMBER':
                return (
                    <NumberField
                        label={t('analytics.reportCreator.filterValue')}
                        value={filterValue}
                        onChange={setFilterValue}
                    />
                );
            case 'DATE':
                return (
                    <DatePicker
                        label={t('analytics.reportCreator.filterValue')}
                        value={filterValue ? parseDate(filterValue) : null}
                        onChange={(date) => setFilterValue(date?.toString())}
                    />
                );
            case 'SINGLE_SELECT':
                return (
                    <Select
                        fullWidth
                        label={t('analytics.reportCreator.filterValue')}
                        selectedKey={filterValue}
                        items={(attrDef.selections || []).map(item => ({
                            id: item.itemValue,
                            name: item.displayName
                        }))}
                        onSelectionChange={setFilterValue}
                    >
                        {(item: SelectItem) => <Item key={item.id}>{item.name}</Item>}
                    </Select>
                );
            default:
                return (
                    <TextField
                        label={t('analytics.reportCreator.filterValue')}
                        value={filterValue || ''}
                        onChange={setFilterValue}
                    />
                );
        }
    }, [filterAttributeName, filterOperator, filterValue, startEnd, getAttributeDefinitionByName, t]);

    // Handle close with unsaved changes check
    const handleClose = useCallback(() => {
        if (hasUnsavedChanges) {
            const confirmLeave = window.confirm(t('templates.editor.confirmLeave'));
            if (!confirmLeave) {
                return;
            }
        }
        navigate('/analytics');
    }, [hasUnsavedChanges, t, navigate]);

    // Handle save
    const handleSave = useCallback(async () => {
        if (!selectedEntity || !selectedAttribute) {
            return;
        }

        // const config = buildReportConfig();

        const request: ReportCreateRequest = {
            reportName: reportName,
            reportDescription: reportDescription,
            groupingEntity: selectedEntity,
            groupingAttribute: selectedAttribute,
            groupingNestedAttribute: selectedNestedAttribute,
            aggregationFunction: aggregationConfig.function as AggregationFunction,
            attributeFilters: reportFilters,
            visualization: {
                type: chartType
            }
        };

        const report = await createReport(request);

        if (report) {
            navigate('/analytics');
        }

        // }, [buildReportConfig]);
    }, []);

    const renderChart = () => {

        const chartTitle = reportName || t('analytics.reportCreator.preview');
        const chartData = previewResult || {data: {}, groupings: []};

        switch (chartType) {
            case ChartType.BAR:
                return (
                    <BarDataChart
                        title={chartTitle}
                        data={chartData}
                        loading={loadingPreview}
                        width={780}
                        height={496}
                    />
                );

            case ChartType.TREE_MAP:
                return (
                    <TreemapDataChart
                        title={chartTitle}
                        data={chartData}
                        loading={loadingPreview}
                    />
                );

            default:
                return (
                    <BarDataChart
                        title={chartTitle}
                        data={chartData}
                        loading={loadingPreview}
                    />
                );
        }
    };

    const handleDateRangeChange = (dateRange: DateRange) => {
        setStartEnd(dateRange);
    };

    return (
        <Box sx={{display: 'flex', height: '95.8vh', overflow: 'hidden'}}>
            {/* Left Panel - Chart Preview */}
            <Box sx={{
                flex: '1 1 60%',
                overflow: 'auto',
                p: 3,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start'
            }}>
                {renderChart()}
            </Box>

            {/* Right Panel - Configuration */}
            <Box
                sx={{
                    width: 400,
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: 'background.paper',
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        p: 2,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        flexShrink: 0,
                    }}
                >
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Typography variant="h6">
                            {t('analytics.reportCreator.title')}
                        </Typography>
                        <IconButton
                            aria-label="Close"
                            onClick={handleClose}
                        >
                            <CloseIcon/>
                        </IconButton>
                    </Box>

                    {hasUnsavedChanges && (
                        <Alert
                            severity="warning"
                            sx={{mt: 1}}
                        >
                            {t('templates.editor.unsavedChanges')}
                        </Alert>
                    )}
                </Box>

                {/* Configuration Content */}
                <Box sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                }}
                >

                    {reportsError && (
                        <ErrorAlert message={reportsError} />
                    )}

                    {/* Basic Configuration */}
                    <Box sx={{p: 2, borderBottom: '1px solid', borderColor: 'divider'}}>
                        <TextField
                            isRequired
                            label={t('analytics.reportCreator.reportName')}
                            value={reportName}
                            onChange={setReportName}
                            placeholder={t('analytics.reportCreator.reportNamePlaceholder')}
                        />

                        <Box sx={{mt: 2}}>
                            <TextField
                                label={t('analytics.reportCreator.reportDescription')}
                                value={reportDescription}
                                onChange={setReportDescription}
                                placeholder={t('analytics.reportCreator.reportDescriptionPlaceholder')}
                            />
                        </Box>

                        <Box sx={{mt: 2}}>

                            <Select
                                fullWidth
                                isRequired
                                label={t('analytics.reportCreator.selectEntity')}
                                placeholder={t('analytics.reportCreator.selectEntityPlaceholder')}
                                selectedKey={selectedEntity}
                                items={attributeEntities}
                                onSelectionChange={(selected) =>
                                    setSelectedEntity(selected as AttributeEntity)
                                }
                            >
                                {(item: { id: AttributeEntity; name: string }) => (
                                    <Item key={item.id}>{item.name}</Item>
                                )}
                            </Select>

                            {selectedEntity && (
                                <NestedSelect
                                    className={'mt-2'}
                                    fullWidth
                                    isRequired
                                    label={t('analytics.reportCreator.selectAttribute')}
                                    placeholder={t('analytics.reportCreator.selectAttributePlaceholder')}
                                    items={mapAttributeDefinitionsToNestedItems(attributeDefinitions)}
                                    value={selectedAttributeValue}
                                    onChange={handleAttributeSelectionChange}
                                    allowsSearch={true}
                                    searchPlaceholder={t('analytics.reportCreator.searchAttributes')}
                                />
                            )}
                        </Box>
                    </Box>

                    {/* Filter Creation Form */}
                    {isCreatingFilter && (
                        <Box sx={{p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50'}}>
                            <Typography variant="subtitle2" sx={{mb: 2, fontWeight: 600}}>
                                {t('analytics.reportCreator.newFilter')}
                            </Typography>

                            <Select
                                fullWidth
                                label={t('analytics.reportCreator.filterAttribute')}
                                placeholder={t('analytics.reportCreator.selectAttributePlaceholder')}
                                selectedKey={filterAttributeName}
                                items={filterableAttributeDefinitions.map(attr => ({
                                    id: attr.name,
                                    name: attr.label
                                }))}
                                onSelectionChange={(key) => setFilterAttributeName(key as string)}
                            >
                                {(item: SelectItem) => <Item key={item.id}>{item.name}</Item>}
                            </Select>

                            <Box sx={{mt: 2}}>
                                <Select
                                    fullWidth
                                    label={t('analytics.reportCreator.filterOperator')}
                                    placeholder={t('analytics.reportCreator.selectOperator')}
                                    selectedKey={filterOperator}
                                    items={Object.values(FilterOperator).map(op => ({
                                        id: op,
                                        name: t(`analytics.reportCreator.operators.${op}`)
                                    }))}
                                    onSelectionChange={(key) => setFilterOperator(key as FilterOperator)}
                                >
                                    {(item: SelectItem) => <Item key={item.id}>{item.name}</Item>}
                                </Select>
                            </Box>

                            {filterAttributeName && filterOperator && (
                                <Box sx={{mt: 2}}>
                                    {getFilterValueInput()}
                                </Box>
                            )}

                            <Box sx={{display: 'flex', gap: 1, mt: 2}}>
                                <Button
                                    onClick={handleCancelFilter}
                                    variant="outlined"
                                    fullWidth
                                >
                                    {t('common.cancel')}
                                </Button>
                                <Button
                                    onClick={handleAddFilter}
                                    variant="contained"
                                    isDisabled={!filterAttributeName || !filterOperator}
                                    fullWidth
                                >
                                    {t('analytics.reportCreator.addFilter')}
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {/* Filters Section */}
                    <Box sx={{p: 2, borderBottom: '1px solid', borderColor: 'divider'}}>
                        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                            <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                                {t('analytics.reportCreator.filters')}
                            </Typography>
                            {!isCreatingFilter && (
                                <Button
                                    onClick={() => setIsCreatingFilter(true)}
                                    variant="outlined"
                                    startIcon={<PlusIcon/>}
                                >
                                    {t('analytics.reportCreator.addFilter')}
                                </Button>
                            )}
                        </Box>

                        {reportFilters.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                {t('analytics.reportCreator.noFilters')}
                            </Typography>
                        ) : (
                            <List sx={{p: 0}}>
                                {reportFilters.map((filter, index) => {
                                    const isComplete = isFilterComplete(filter);
                                    return (
                                        <React.Fragment key={index}>
                                            {index > 0 && <Divider/>}
                                            <ListItem
                                                sx={{
                                                    px: 0,
                                                    py: 1.5,
                                                    '&:hover': {
                                                        bgcolor: 'action.hover'
                                                    }
                                                }}
                                            >
                                                <Box sx={{flex: 1, pr: 6}}>
                                                    <ListItemText
                                                        primary={
                                                            <Box sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                mb: 0.5
                                                            }}>
                                                                <Typography variant="body2">
                                                                    {getFilterLabel(filter)}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                        secondary={
                                                            <Box sx={{
                                                                display: 'flex',
                                                                gap: 0.5,
                                                                flexWrap: 'wrap',
                                                                mt: 0.5
                                                            }}>
                                                                {isComplete ? (
                                                                    <Chip
                                                                        label={t('analytics.reportCreator.active')}
                                                                        size="small"
                                                                    />
                                                                ) : (
                                                                    <Chip
                                                                        label={t('analytics.reportCreator.incomplete')}
                                                                        size="small"
                                                                        color="warning"
                                                                    />
                                                                )}
                                                            </Box>
                                                        }
                                                    />
                                                    <ListItemSecondaryAction>
                                                        <IconButton
                                                            aria-label="Delete filter"
                                                            size="small"
                                                            onClick={() => handleRemoveFilter(filter)}
                                                        >
                                                            <IconDeleteInRow/>
                                                        </IconButton>
                                                    </ListItemSecondaryAction>
                                                </Box>
                                            </ListItem>
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        )}
                    </Box>

                    {/* Grouping Section */}
                    {selectedAttribute && selectedAttributeConfig?.grouping?.supported && (
                        <Box sx={{p: 2, borderBottom: '1px solid', borderColor: 'divider'}}>
                            <Typography variant="subtitle2" sx={{mb: 2, fontWeight: 600}}>
                                {t('analytics.reportCreator.grouping')}
                            </Typography>

                            <Checkbox
                                isSelected={groupingConfig.enabled}
                                onChange={(enabled) => setGroupingConfig(prev => ({
                                    ...prev,
                                    enabled
                                }))}
                            >
                                {t('analytics.reportCreator.enableGrouping')}
                            </Checkbox>

                            {groupingConfig.enabled && selectedAttributeConfig.grouping?.modes && (
                                <Box sx={{mt: 2}}>
                                    <Select
                                        fullWidth
                                        label={t('analytics.reportCreator.groupingMode')}
                                        placeholder={t('analytics.reportCreator.selectMode')}
                                        selectedKey={groupingConfig.mode}
                                        items={selectedAttributeConfig.grouping.modes.map((mode: string) => ({
                                            id: mode,
                                            name: reportableFieldsSchema.globalSettings.groupingModes[mode] || mode
                                        }))}
                                        onSelectionChange={(value) => setGroupingConfig(prev => ({
                                            ...prev,
                                            mode: value as string
                                        }))}
                                    >
                                        {(item: SelectItem) => <Item key={item.id}>{item.name}</Item>}
                                    </Select>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Aggregation Section */}
                    {selectedAttribute && selectedAttributeConfig?.aggregations?.supported && (
                        <Box sx={{p: 2, borderBottom: '1px solid', borderColor: 'divider'}}>
                            <Typography variant="subtitle2" sx={{mb: 2, fontWeight: 600}}>
                                {t('analytics.reportCreator.aggregation')}
                            </Typography>

                            <Select
                                fullWidth={true}
                                label={t('analytics.reportCreator.aggregationFunction')}
                                placeholder={t('analytics.reportCreator.selectFunction')}
                                selectedKey={aggregationConfig.function}
                                items={selectedAttributeConfig.aggregations.operations.map((fn: string) => ({
                                    id: fn,
                                    name: fn
                                }))}
                                onSelectionChange={(value) => setAggregationConfig(prev => ({
                                    ...prev,
                                    function: value as string
                                }))}
                            >
                                {(item: SelectItem) => <Item
                                    key={item.id}>{t(`analytics.reportCreator.aggregations.${item.name}`)}</Item>}
                            </Select>
                        </Box>
                    )}

                    {/* Visualization Section */}
                    <Box sx={{p: 2, borderBottom: '1px solid', borderColor: 'divider'}}>
                        <Typography variant="subtitle2" sx={{mb: 2, fontWeight: 600}}>
                            {t('analytics.reportCreator.visualization')}
                        </Typography>

                        <Select
                            fullWidth
                            label={t('analytics.reportCreator.chartType')}
                            placeholder={t('analytics.reportCreator.selectChartType')}
                            selectedKey={chartType}
                            items={[
                                {id: ChartType.BAR, name: t('analytics.reportCreator.chartTypes.bar')},
                                {id: ChartType.TREE_MAP, name: t('analytics.reportCreator.chartTypes.treeMap')},
                            ]}
                            onSelectionChange={(value) => setChartType(value as ChartType)}
                        >
                            {(item: SelectItem) => <Item key={item.id}>{item.name}</Item>}
                        </Select>
                    </Box>
                </Box>

                {/* Footer - Actions */}
                <Box
                    sx={{
                        p: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        flexShrink: 0,
                    }}
                >
                    <Box sx={{display: 'flex', gap: 1}}>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            color="primary"
                            isDisabled={saving}
                            fullWidth
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            color="primary"
                            isDisabled={saving || !reportName || !selectedAttribute}
                            fullWidth
                        >
                            {saving ? t('common.saving') : t('common.save')}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default ReportCreator;